/**
 * Half past midnight, and which day this food belongs to.
 *
 * A day is a stored local date, computed at write time and never recomputed.
 * That is right in every case except the one that happens most: eating after
 * midnight. The app silently files supper under tomorrow, the evening's numbers
 * reset in the middle of the evening, and nothing on screen admits that a
 * boundary was crossed — so the first sign of it is a diary with an empty
 * Thursday and a Friday that starts with a bowl of cereal at 00:32.
 *
 * The fix is not to move the boundary. It is to say where the boundary is,
 * while the ambiguity is real, and let the person who was actually there
 * decide. Yesterday's closing numbers come along because the choice is
 * otherwise uninformed: "log to Thursday" means something different if Thursday
 * closed 500 under than if it closed 200 over.
 *
 * The offer expires at 04:00. Past that, late is just early.
 */

import { LATE_NIGHT_UNTIL_HOUR, type DaySummary } from "@calorio/core";

interface LateNightProps {
  /** The date the app would file a new entry under right now. */
  today: string;
  /** The evening that has just ended, and how it closed. */
  yesterday: string;
  yesterdaySummary: DaySummary | undefined;
  /** Which day new entries are currently being written to. */
  writingTo: string;
  onChoose: (date: string) => void;
}

const dayName = (isoDate: string) =>
  new Date(`${isoDate}T12:00:00Z`).toLocaleDateString(undefined, { weekday: "long" });

export function LateNight({
  today,
  yesterday,
  yesterdaySummary,
  writingTo,
  onChoose,
}: LateNightProps) {
  const closed = yesterdaySummary;
  const target = closed?.target?.kcal ?? null;
  const eaten = closed ? Math.round(closed.totals.kcal) : null;
  const gap = target != null && eaten != null ? target - eaten : null;

  return (
    <div className="late">
      <span className="late__label">Still up?</span>

      <p className="late__body">
        It&rsquo;s after midnight, so anything you log lands on{" "}
        <strong>{dayName(today)}</strong>.
        {eaten != null && target != null && (
          <>
            {" "}
            {dayName(yesterday)} closed at{" "}
            <span className="num">
              {eaten.toLocaleString()} of {target.toLocaleString()}
            </span>
            {gap != null && (
              <>
                , <span className="num">{Math.abs(Math.round(gap)).toLocaleString()}</span>{" "}
                {gap >= 0 ? "under" : "over"}
              </>
            )}
            .
          </>
        )}
      </p>

      {/* Two equal buttons, because neither is the safe default. The app's own
          answer is on the left with the dark fill; the other is a real choice
          and not a link tucked underneath. */}
      <div className="late__choice">
        <button
          className={`late__button late__button--keep${writingTo === today ? " is-on" : ""}`}
          onClick={() => onChoose(today)}
        >
          Log to {dayName(today)}
        </button>
        <button
          className={`late__button late__button--move${writingTo === yesterday ? " is-on" : ""}`}
          onClick={() => onChoose(yesterday)}
        >
          Log to {dayName(yesterday)}
        </button>
      </div>

      <p className="late__note">
        Offered until{" "}
        <span className="num">
          {String(LATE_NIGHT_UNTIL_HOUR).padStart(2, "0")}:00
        </span>
        . After that, late is just early.
      </p>
    </div>
  );
}
